
import {
  defineComponent, ref, computed, onMounted, reactive, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import EditorModal from '@/components/modals/EditorModal.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toast from 'primevue/toast';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import {
  fetchAvailableSources,
  fetchAvailableFields,
  createKpi,
  updateKpi,
  fetchKpi,
  previewKpi,
} from '@/composables/customStats/customKpi';
import {
  AGGREGATIONS_BY_FIELD_TYPE,
  ALL_AGGREGATION_TYPES,
  DEFAULT_LOOKBACK_MONTHS,
  DIMENSION_ELIGIBLE_TYPES,
  MAX_DIMENSIONS_PER_KPI,
  MAX_FILTERS,
  MAX_LOOKBACK_MONTHS,
  MAX_MEASURES_PER_KPI,
  TIME_FIELD_ELIGIBLE_TYPES,
} from '@/composables/customStats/customKpi.interfaces';
import type {
  AggregationType,
  AvailableField,
  AvailableSource,
  CreateCustomKpiPayload,
  CustomKpiDefinition,
  KpiDimension,
  KpiFilter,
  KpiMeasure,
  PreviewResult,
  SourceType,
} from '@/composables/customStats/customKpi.interfaces';
import {
  CUSTOM_KPI_TEMPLATES,
  CUSTOM_KPI_TEMPLATE_GROUP_ORDER,
} from '@/composables/customStats/customKpiTemplates';
import type { CustomKpiTemplate } from '@/composables/customStats/customKpiTemplates';

type StepKey =
  | 'source'
  | 'relations'
  | 'measures'
  | 'dimensions'
  | 'time'
  | 'filters'
  | 'display';

const SYSTEM_SOURCE_META: Record<string, { icon: string; labelKey: string; sublabelKey: string }> = {
  orders: { icon: 'far fa-cart-shopping', labelKey: 'customStats.source.system.orders', sublabelKey: 'customStats.source.system.orders_sub' },
  contacts: { icon: 'far fa-user', labelKey: 'customStats.source.system.contacts', sublabelKey: 'customStats.source.system.contacts_sub' },
  products: { icon: 'far fa-box-open', labelKey: 'customStats.source.system.products', sublabelKey: 'customStats.source.system.products_sub' },
  carts: { icon: 'far fa-basket-shopping', labelKey: 'customStats.source.system.carts', sublabelKey: 'customStats.source.system.carts_sub' },
  orders_products: { icon: 'far fa-cart-flatbed', labelKey: 'customStats.source.system.orders_products', sublabelKey: 'customStats.source.system.orders_products_sub' },
  carts_products: { icon: 'far fa-basket-shopping', labelKey: 'customStats.source.system.carts_products', sublabelKey: 'customStats.source.system.carts_products_sub' },
  contact_device: { icon: 'far fa-mobile-screen', labelKey: 'customStats.source.system.contact_device', sublabelKey: 'customStats.source.system.contact_device_sub' },
  manufacturers: { icon: 'far fa-tag', labelKey: 'customStats.source.system.manufacturers', sublabelKey: 'customStats.source.system.manufacturers_sub' },
  carriers: { icon: 'far fa-truck', labelKey: 'customStats.source.system.carriers', sublabelKey: 'customStats.source.system.carriers_sub' },
  categories: { icon: 'far fa-folder-tree', labelKey: 'customStats.source.system.categories', sublabelKey: 'customStats.source.system.categories_sub' },
  product_attributes: { icon: 'far fa-shapes', labelKey: 'customStats.source.system.product_attributes', sublabelKey: 'customStats.source.system.product_attributes_sub' },
  vouchers: { icon: 'far fa-ticket', labelKey: 'customStats.source.system.vouchers', sublabelKey: 'customStats.source.system.vouchers_sub' },
  customers_groups_list: { icon: 'far fa-users', labelKey: 'customStats.source.system.customers_groups_list', sublabelKey: 'customStats.source.system.customers_groups_list_sub' },
};

const AGGREGATION_META: Record<AggregationType, { icon: string; labelKey: string; sublabelKey: string }> = {
  count: { icon: 'far fa-hashtag', labelKey: 'customStats.aggCard.count.label', sublabelKey: 'customStats.aggCard.count.sublabel' },
  count_distinct: { icon: 'far fa-fingerprint', labelKey: 'customStats.aggCard.count_distinct.label', sublabelKey: 'customStats.aggCard.count_distinct.sublabel' },
  sum: { icon: 'far fa-sigma', labelKey: 'customStats.aggCard.sum.label', sublabelKey: 'customStats.aggCard.sum.sublabel' },
  avg: { icon: 'far fa-chart-mixed', labelKey: 'customStats.aggCard.avg.label', sublabelKey: 'customStats.aggCard.avg.sublabel' },
  min: { icon: 'far fa-arrow-down-to-line', labelKey: 'customStats.aggCard.min.label', sublabelKey: 'customStats.aggCard.min.sublabel' },
  max: { icon: 'far fa-arrow-up-to-line', labelKey: 'customStats.aggCard.max.label', sublabelKey: 'customStats.aggCard.max.sublabel' },
};

export default defineComponent({
  name: 'CustomKpiEditor',
  components: {
    EditorModal,
    SpmButton,
    SpmMessage,
    SpmBadge,
    LocalizedTextInput,
    Dropdown,
    InputText,
    InputNumber,
    Textarea,
    Button,
    DataTable,
    Column,
    Toast,
    Dialog,
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();

    const editId = computed(() => (route.name === 'custom-kpis.edit' ? (route.params.id as string) : null));
    const duplicateId = computed(() => (route.query.duplicate as string | undefined));
    const isEdit = computed(() => !!editId.value);

    // ----- Form state -----
    const makeDefaultMeasure = (): KpiMeasure => ({
      type: 'count',
      field: undefined,
      format: 'number',
      suffix: null,
      label: { fr: '', en: '' },
    });

    const makeDefaultDimension = (): KpiDimension => ({
      field: '',
      label: { fr: '', en: '' },
    });

    const form = reactive<CreateCustomKpiPayload>({
      name: {},
      description: {},
      measures: [makeDefaultMeasure()],
      dimensions: [],
      source_type: 'custom',
      source_schema_name: '',
      relations_chain: [],
      time_field: '',
      time_field_explicit: true,
      filters: [],
      lookback_max_months: DEFAULT_LOOKBACK_MONTHS,
    });

    const availableSources = ref<AvailableSource[]>([]);
    const availableFields = ref<AvailableField[]>([]);
    const isLoadingFields = ref(false);
    const isSaving = ref(false);
    const isPreviewLoading = ref(false);
    const previewResult = ref<PreviewResult | null>(null);

    // Multi-langue : pour V1 fr+en. À étendre selon la conf shop.
    const shopLangs = ['fr', 'en'];
    const nameByLang = reactive<Record<string, string>>({ fr: '', en: '' });
    const descByLang = reactive<Record<string, string>>({ fr: '', en: '' });
    const filterValueProxy = reactive<Record<number, string>>({});

    // Toggles UI
    const timeEnabled = ref(false);

    // ----- Steps -----
    const stepKeys: StepKey[] = ['source', 'relations', 'measures', 'dimensions', 'time', 'filters', 'display'];
    const currentStepIndex = ref(0);

    const visibleSteps = computed<StepKey[]>(() => stepKeys);

    const currentStep = computed<StepKey>(() => visibleSteps.value[currentStepIndex.value] ?? 'source');
    const currentStepNumber = computed(() => currentStepIndex.value + 1);
    const isLastStep = computed(() => currentStepIndex.value === visibleSteps.value.length - 1);
    const canGoBack = computed(() => currentStepIndex.value > 0);
    const progressPercent = computed(() => {
      const total = visibleSteps.value.length;
      return total > 0 ? Math.round(((currentStepIndex.value + 1) / total) * 100) : 0;
    });

    // ----- Source cards -----
    const systemSourceCards = computed(() => availableSources.value
      .filter((s) => s.type === 'system')
      .map((s) => {
        const meta = SYSTEM_SOURCE_META[s.schemaName];
        return {
          ...s,
          icon: meta?.icon ?? 'far fa-database',
          label: meta ? t(meta.labelKey) : s.label,
          sublabel: meta ? t(meta.sublabelKey) : s.tableFqn,
        };
      }));

    const customDataSources = computed(() => availableSources.value
      .filter((s) => s.type === 'custom')
      .map((s) => ({
        ...s,
        icon: 'far fa-table',
        label: s.label,
        sublabel: s.tableFqn,
      })));

    // Mapping schema → groupe thématique pour l'affichage en step 1.
    // Tout schema absent du mapping va dans 'sales' par défaut (sécu).
    const SOURCE_GROUP_BY_SCHEMA: Record<string, 'sales' | 'catalog' | 'customers'> = {
      orders: 'sales',
      orders_products: 'sales',
      carts: 'sales',
      carts_products: 'sales',
      vouchers: 'sales',
      products: 'catalog',
      product_attributes: 'catalog',
      manufacturers: 'catalog',
      categories: 'catalog',
      carriers: 'catalog',
      contacts: 'customers',
      contact_device: 'customers',
      customers_groups_list: 'customers',
    };
    const SOURCE_GROUP_ORDER: Array<'sales' | 'catalog' | 'customers' | 'custom'> = [
      'sales',
      'catalog',
      'customers',
      'custom',
    ];

    const sourceGroups = computed(() => {
      const buckets: Record<string, ReturnType<typeof systemSourceCards.value.slice>> = {
        sales: [],
        catalog: [],
        customers: [],
        custom: [],
      };
      systemSourceCards.value.forEach((card) => {
        const groupKey = SOURCE_GROUP_BY_SCHEMA[card.schemaName] ?? 'sales';
        buckets[groupKey].push(card);
      });
      if (customDataSources.value.length > 0) {
        buckets.custom.push({
          type: 'custom' as SourceType,
          schemaName: '__custom_aggregator__',
          label: t('customStats.source.customMine'),
          sublabel: t('customStats.source.customMineSub', { count: customDataSources.value.length }),
          icon: 'far fa-folder-open',
          tableFqn: '',
        });
      }
      return SOURCE_GROUP_ORDER
        .filter((key) => buckets[key].length > 0)
        .map((key) => ({
          key,
          label: t(`customStats.source.groups.${key}`),
          cards: buckets[key],
        }));
    });

    const sourceCards = computed(() => {
      const cards = [...systemSourceCards.value];
      if (customDataSources.value.length > 0) {
        cards.push({
          type: 'custom' as SourceType,
          schemaName: '__custom_aggregator__',
          label: t('customStats.source.customMine'),
          sublabel: t('customStats.source.customMineSub', { count: customDataSources.value.length }),
          icon: 'far fa-folder-open',
          tableFqn: '',
        });
      }
      return cards;
    });

    // ----- Relations available on current source -----
    // On lit les relationships exposées par la source (système ou custom)
    // depuis l'objet AvailableSource déjà chargé. Le backend renvoie pour
    // chaque source les couples {propertyName, targetSchema, type}.
    const RELATION_ICONS: Record<string, string> = {
      products: 'far fa-box-open',
      product: 'far fa-box-open',
      contacts: 'far fa-user',
      contact: 'far fa-user',
      orders: 'far fa-cart-shopping',
      order: 'far fa-cart-shopping',
      carts: 'far fa-basket-shopping',
      cart: 'far fa-basket-shopping',
      manufacturer: 'far fa-tag',
      carrier: 'far fa-truck',
      categories: 'far fa-folder-tree',
      product_categories: 'far fa-folder-tree',
      main_category: 'far fa-folder',
    };

    const safeName = (propertyName: string | undefined, targetSchema: string | undefined): string => (
      propertyName && propertyName.trim() ? propertyName
        : (targetSchema && targetSchema.trim() ? targetSchema : 'related')
    );

    const relationLabel = (propertyName: string, targetSchema: string): string => {
      const name = safeName(propertyName, targetSchema);
      const key = `customStats.relations.${name}.label`;
      const translated = t(key);
      if (translated !== key) return translated;
      // Fallback : si name === targetSchema, n'affiche pas "(targetSchema)" en doublon
      return name === targetSchema ? name : `${name} (${targetSchema})`;
    };

    const relationSublabel = (propertyName: string, targetSchema: string, relType: string): string => {
      const name = safeName(propertyName, targetSchema);
      const key = `customStats.relations.${name}.sublabel`;
      const translated = t(key);
      if (translated !== key) return translated;
      // Fallback générique
      const dirKey = relType === 'one-to-many'
        ? 'customStats.relations.genericOneToMany'
        : 'customStats.relations.genericManyToOne';
      return t(dirKey, { target: targetSchema || name });
    };

    const availableRelations = computed(() => {
      const currentSource = availableSources.value.find(
        (s) => s.type === form.source_type && s.schemaName === form.source_schema_name,
      );
      const rels = currentSource?.relationships ?? [];
      return rels
        // Skip relationships malformées (sans target identifiable)
        .filter((r) => r.propertyName || r.targetSchema)
        .map((r) => {
          const name = safeName(r.propertyName, r.targetSchema);
          return {
            name,
            icon: RELATION_ICONS[name] ?? RELATION_ICONS[r.targetSchema] ?? 'far fa-link',
            label: relationLabel(r.propertyName, r.targetSchema),
            sublabel: relationSublabel(r.propertyName, r.targetSchema, r.type),
            cardinality: r.type === 'one-to-many' ? 'many' : 'one',
          };
        });
    });

    // True dès qu'au moins une relation one-to-many est cochée — sert à
    // afficher un bandeau explicatif sur les étapes Mesures / Dimensions.
    const hasOneToManyRelationSelected = computed(() =>
      availableRelations.value.some(
        (r) => r.cardinality === 'many' && (form.relations_chain ?? []).includes(r.name),
      ),
    );

    // ----- Aggregation cards (par mesure) -----
    const aggregationCardsForMeasure = (measure: KpiMeasure) => ALL_AGGREGATION_TYPES.map((value) => {
      const meta = AGGREGATION_META[value];
      const compatible = !measure.field
        || (AGGREGATIONS_BY_FIELD_TYPE[
          availableFields.value.find((f) => f.path === measure.field)?.type ?? ''
        ] ?? []).includes(value);
      return {
        value,
        icon: meta.icon,
        label: t(meta.labelKey),
        sublabel: t(meta.sublabelKey),
        compatible,
      };
    });

    // Les champs issus d'une relation one-to-many (relationCardinality='many')
    // ne peuvent pas servir de mesure / dimension / time_field car cela
    // dupliquerait les rows main. Ils restent utilisables comme **filtre**
    // uniquement (le backend les transforme en EXISTS).
    const isOneToManyField = (f: AvailableField) =>
      f.relationCardinality === 'many';

    // ----- Field options (par mesure : filtre sur le type d'agrégation choisi) -----
    const aggregationFieldOptionsForMeasure = (measure: KpiMeasure) => availableFields.value.filter((f) => {
      if (isOneToManyField(f)) return false;
      const compat = AGGREGATIONS_BY_FIELD_TYPE[f.type] ?? [];
      return compat.includes(measure.type);
    });

    const dimensionFieldOptions = computed(() => availableFields.value.filter(
      (f) => !isOneToManyField(f) && DIMENSION_ELIGIBLE_TYPES.includes(f.type),
    ));

    const timeFieldOptions = computed(() => availableFields.value.filter(
      (f) => !isOneToManyField(f) && TIME_FIELD_ELIGIBLE_TYPES.includes(f.type),
    ));

    // ----- Helpers -----
    const aggregationLabel = (agg: AggregationType): string => {
      const meta = AGGREGATION_META[agg];
      return meta ? t(meta.labelKey) : agg;
    };

    const typeLabel = (type: string): string => t(`customStats.fieldType.${type}`, type);

    /**
     * Display label with relation prefix when the field comes from a joined
     * source. Example: "Pays" → "Pays (du contact)" when contact_country
     * est fetché via la relation `contacts`.
     */
    const displayLabel = (field: AvailableField): string => {
      if (field.source !== 'relation' || !field.relationPath?.length) {
        return field.label;
      }
      const chain = field.relationPath.join(' › ');
      return `${field.label} (${chain})`;
    };

    // Hints métier pour les champs avec une sémantique non-évidente
    // (calculés, expressions, libellés normalisés…). Affichés en tooltip
    // sur les options des dropdowns mesure / dim / time.
    // Clé = path complet (ex: 't.amount_eur') ou colonne nue (ex: 'amount_eur').
    const FIELD_HINTS: Record<string, { fr: string; en: string }> = {
      amount_eur: {
        fr: 'Converti en EUR (amount × currency_rate_to_euro). Utilisez ce champ pour cumuler des shops multi-devises.',
        en: 'Converted to EUR (amount × currency_rate_to_euro). Use this field to aggregate multi-currency shops.',
      },
      stock_value: {
        fr: 'Calculé : prix × quantité. Somme = valeur totale du stock.',
        en: 'Computed: price × quantity. Sum gives total stock value.',
      },
      discount_pct: {
        fr: 'Pourcentage de remise effectif sur le prix promo. Moyenne sur le catalogue donne la remise moyenne.',
        en: 'Effective discount percentage on the promo price. Average across the catalog gives mean discount.',
      },
      line_total: {
        fr: 'Calculé : prix unitaire × quantité de la ligne. Somme par produit/marque/catégorie = CA correspondant.',
        en: 'Computed: unit price × line quantity. Sum per product/brand/category = corresponding revenue.',
      },
      discount_ratio: {
        fr: 'Pourcentage de remise sur la commande (voucher_amount / amount × 100). Moyenne sur les commandes utilisant un voucher.',
        en: 'Discount percentage on the order (voucher_amount / amount × 100). Average across orders using a voucher.',
      },
      cart_to_order_minutes: {
        fr: 'Délai en minutes entre la date du panier et la commande. Moyenne mesure la friction d\'achat.',
        en: 'Time in minutes between cart and order date. Average measures purchase friction.',
      },
      order_hour: {
        fr: 'Heure (0-23) de la commande. Idéal en dimension pour identifier les pics d\'activité.',
        en: 'Hour (0-23) of the order. Ideal as a dimension to identify activity spikes.',
      },
      order_dow: {
        fr: 'Jour de la semaine (Lundi, Mardi…). Dimension pour identifier les jours forts.',
        en: 'Day of week (Monday, Tuesday…). Dimension to identify peak days.',
      },
      amount_bucket: {
        fr: 'Tranches de montant : < 50€, 50-100€, 100-200€, 200-500€, > 500€.',
        en: 'Amount buckets: < €50, €50-100, €100-200, €200-500, > €500.',
      },
      cart_status: {
        fr: 'Converti / Abandonné, basé sur is_valid. Dimension clé pour le taux d\'abandon.',
        en: 'Converted / Abandoned, based on is_valid. Key dimension for abandon rate.',
      },
      in_stock_label: {
        fr: 'Oui (quantity > 0) / Non (quantity = 0). Dimension simple pour la disponibilité.',
        en: 'Yes (quantity > 0) / No (quantity = 0). Simple dimension for availability.',
      },
      stock_level: {
        fr: 'Rupture / Bas / Moyen / Élevé. Tranches de niveau de stock.',
        en: 'Out / Low / Medium / High. Stock level buckets.',
      },
      has_discount_label: {
        fr: 'En promo / Plein tarif, basé sur la présence d\'un price_discount inférieur au price.',
        en: 'On sale / Full price, based on price_discount being lower than price.',
      },
      has_voucher_label: {
        fr: 'Oui / Non — la commande contient-elle un code promo.',
        en: 'Yes / No — does the order contain a voucher.',
      },
      spm_voucher_used_label: {
        fr: 'SPM / Externe — origine du code promo utilisé.',
        en: 'SPM / External — source of the voucher used.',
      },
      is_valid_label: {
        fr: 'Validée / Annulée — état final de la commande.',
        en: 'Validated / Cancelled — final order state.',
      },
      active_label: {
        fr: 'Actif / Inactif — état actuel.',
        en: 'Active / Inactive — current state.',
      },
      category_active_label: {
        fr: 'Active / Inactive — état de la catégorie.',
        en: 'Active / Inactive — category state.',
      },
      category_level_label: {
        fr: 'Racine / Sous-catégorie — niveau dans l\'arborescence.',
        en: 'Root / Sub-category — level in the tree.',
      },
      manufacturer_active_label: {
        fr: 'Active / Inactive — état de la marque.',
        en: 'Active / Inactive — brand state.',
      },
      // Voucher fields
      type_voucher_label: {
        fr: 'Réduction fixe / Pourcentage / Frais de port offerts — type de code émis.',
        en: 'Fixed amount / Percentage / Free shipping — type of voucher issued.',
      },
      validity_label: {
        fr: 'À venir / Actif / Expiré — basé sur date_from et date_to comparés à NOW().',
        en: 'Upcoming / Active / Expired — based on date_from and date_to vs NOW().',
      },
      used_label: {
        fr: 'Utilisé / Non utilisé — le code a-t-il été consommé. Ratio = taux d\'utilisation.',
        en: 'Used / Unused — has the voucher been consumed. Ratio = usage rate.',
      },
      origin_label: {
        fr: 'ShopiMind / Boutique — un code est ShopiMind si son code commence par « SPM_ », sinon il provient de la boutique.',
        en: 'ShopiMind / Shop — a code is ShopiMind-issued if it starts with "SPM_", otherwise it comes from the shop.',
      },
      // Group fields
      group_active_label: {
        fr: 'Actif / Inactif — état du groupe client.',
        en: 'Active / Inactive — customer group state.',
      },
      group_name: {
        fr: 'Nom du groupe principal du contact (appartenance la plus récente). Idéal en dimension pour CA par groupe.',
        en: 'Main group name of the contact (most recent membership). Ideal as dimension for revenue per group.',
      },
      // Variant fields
      combination_name: {
        fr: 'Nom de la déclinaison (ex: « Rouge - Taille L »). Idéal en dimension pour ventiler par variante.',
        en: 'Variant name (ex: "Red - Size L"). Ideal as dimension to break down by variant.',
      },
      default_label: {
        fr: 'Par défaut / Alternative — la variante est-elle celle proposée par défaut au catalogue.',
        en: 'Default / Alternative — is this the variant shown by default in the catalog.',
      },
      // Order status
      status_name: {
        fr: 'Libellé humain du statut, configuré par le marchand (ex: « Expédiée », « En attente de paiement »).',
        en: 'Human-readable status label, configured by the merchant (ex: "Shipped", "Awaiting payment").',
      },
    };

    const fieldHint = (field: AvailableField): string => {
      const hint = FIELD_HINTS[field.column];
      const lang = locale.value === 'en' ? 'en' : 'fr';
      const sqlPath = field.path;
      if (hint) {
        return `${sqlPath}\n\n${hint[lang]}`;
      }
      return sqlPath;
    };

    // Formate un nombre avec séparateurs de milliers selon la locale.
    const formatNumber = (n: number): string => {
      if (!Number.isFinite(n)) return String(n);
      try {
        return new Intl.NumberFormat(locale.value).format(n);
      } catch {
        return String(n);
      }
    };

    // Projection annualisée du nb de rows scannées : `scanned × (365 / previewDays)`.
    const estimatedYearlyRows = (preview: PreviewResult): number => {
      if (!preview.sourceRowsScanned || !preview.previewDays) return 0;
      return Math.round(preview.sourceRowsScanned * (365 / preview.previewDays));
    };

    const filterOperatorOptions = [
      { value: 'eq', label: t('customStats.filterOp.eq') },
      { value: 'neq', label: t('customStats.filterOp.neq') },
      { value: 'gt', label: t('customStats.filterOp.gt') },
      { value: 'gte', label: t('customStats.filterOp.gte') },
      { value: 'lt', label: t('customStats.filterOp.lt') },
      { value: 'lte', label: t('customStats.filterOp.lte') },
      { value: 'in', label: t('customStats.filterOp.in') },
      { value: 'not_in', label: t('customStats.filterOp.not_in') },
      { value: 'contains', label: t('customStats.filterOp.contains') },
      { value: 'starts_with', label: t('customStats.filterOp.starts_with') },
      { value: 'ends_with', label: t('customStats.filterOp.ends_with') },
      { value: 'is_null', label: t('customStats.filterOp.is_null') },
      { value: 'is_not_null', label: t('customStats.filterOp.is_not_null') },
    ];

    // ----- Step navigation -----
    const isMeasureValid = (m: KpiMeasure): boolean => {
      const labelOk = shopLangs.some((l) => (m.label?.[l] ?? '').trim().length > 0);
      if (!labelOk) return false;
      if (m.type === 'count') return true;
      return !!m.field;
    };

    const isDimensionValid = (d: KpiDimension): boolean => {
      const labelOk = shopLangs.some((l) => (d.label?.[l] ?? '').trim().length > 0);
      return labelOk && !!d.field;
    };

    const canGoNext = computed(() => {
      const step = currentStep.value;
      if (step === 'source') return !!form.source_schema_name && form.source_schema_name !== '__custom_aggregator__';
      if (step === 'measures') return form.measures.length > 0 && form.measures.every(isMeasureValid);
      if (step === 'dimensions') return (form.dimensions ?? []).every(isDimensionValid);
      if (step === 'time') return !timeEnabled.value || !!form.time_field;
      return true; // relations / filters / display
    });

    const canSave = computed(() => {
      const hasName = shopLangs.some((l) => (nameByLang[l] ?? '').trim().length > 0);
      return hasName
        && !!form.source_schema_name
        && form.measures.length > 0
        && form.measures.every(isMeasureValid)
        && (form.dimensions ?? []).every(isDimensionValid);
    });

    const canRunPreview = computed(() => !!form.source_schema_name && form.measures.length > 0 && !isEdit.value);

    // ----- Modal title -----
    const modalTitle = computed(() => (isEdit.value
      ? t('customStats.editor.editTitle')
      : t('customStats.editor.createTitle')));
    const modalSubtitle = computed(() => (isEdit.value ? t('customStats.editor.editLockNotice') : ''));

    // ----- Summary lines (live preview) -----
    const summarySource = computed(() => {
      if (!form.source_schema_name) return '';
      const meta = SYSTEM_SOURCE_META[form.source_schema_name];
      return meta ? t(meta.labelKey) : form.source_schema_name;
    });
    const summaryRelations = computed(() => t('customStats.editor.preview.line.relations', {
      list: (form.relations_chain ?? []).join(', '),
    }));
    const fieldLabelByPath = (path?: string): string => {
      if (!path) return '';
      const f = availableFields.value.find((x) => x.path === path);
      return f ? displayLabel(f) : (path.split('.').pop() ?? '');
    };

    const summaryMeasures = computed(() => form.measures.map((m) => {
      const label = m.label?.[locale.value] || m.label?.fr || m.label?.en || '';
      const aggLabel = aggregationLabel(m.type);
      const fieldLbl = m.field ? ` (${fieldLabelByPath(m.field)})` : '';
      return label ? `${label} : ${aggLabel}${fieldLbl}` : `${aggLabel}${fieldLbl}`;
    }));
    const summaryDimensions = computed(() => {
      const dims = form.dimensions ?? [];
      const labels = dims
        .map((d) => d.label?.[locale.value] || d.label?.fr || d.label?.en || fieldLabelByPath(d.field))
        .filter(Boolean);
      return t('customStats.editor.preview.line.dimensions', {
        list: labels.join(', '),
      });
    });
    const summaryTime = computed(() => {
      const fl = fieldLabelByPath(form.time_field) || form.time_field;
      return t('customStats.editor.preview.line.time', { field: fl });
    });
    const summaryFilters = computed(() => t('customStats.editor.preview.line.filters', {
      count: form.filters?.length ?? 0,
    }));

    // ----- Lifecycle -----
    onMounted(async () => {
      try {
        availableSources.value = await fetchAvailableSources();
      } catch (err: any) {
        toast.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to load sources', life: 5000 });
      }

      if (editId.value) await loadExistingKpi(editId.value, false);
      else if (duplicateId.value) await loadExistingKpi(duplicateId.value, true);
    });

    async function loadFields() {
      if (!form.source_schema_name) {
        availableFields.value = [];
        return;
      }
      isLoadingFields.value = true;
      try {
        availableFields.value = await fetchAvailableFields(
          form.source_type,
          form.source_schema_name,
          form.relations_chain ?? [],
        );
      } catch (err: any) {
        toast.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to load fields', life: 5000 });
        availableFields.value = [];
      } finally {
        isLoadingFields.value = false;
      }
    }

    async function loadExistingKpi(id: string, asDuplicate: boolean) {
      try {
        const kpi = await fetchKpi(id);
        fillFormFromKpi(kpi);
        if (asDuplicate) {
          form.lookback_max_months = DEFAULT_LOOKBACK_MONTHS;
          shopLangs.forEach((l) => {
            if (nameByLang[l]) nameByLang[l] = `${nameByLang[l]} (copy)`;
          });
        }
        await loadFields();
      } catch (err: any) {
        toast.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to load KPI', life: 5000 });
      }
    }

    function fillFormFromKpi(kpi: CustomKpiDefinition) {
      form.source_type = kpi.source_type;
      form.source_schema_name = kpi.source_schema_name;
      form.relations_chain = kpi.relations_chain ?? [];
      form.time_field = kpi.time_field ?? '';
      form.time_field_explicit = kpi.time_field_explicit ?? true;
      form.filters = (kpi.filters_json ?? []).map((f) => ({ ...f }));
      form.measures = (kpi.measures ?? []).map((m) => ({
        id_measure: m.id_measure,
        type: m.type,
        field: m.field,
        format: m.format ?? 'number',
        suffix: m.suffix ?? null,
        label: { ...(m.label ?? {}) },
        position: m.position,
      }));
      if (form.measures.length === 0) form.measures = [makeDefaultMeasure()];
      form.dimensions = (kpi.dimensions ?? []).map((d) => ({
        id_dimension: d.id_dimension,
        field: d.field,
        label: { ...(d.label ?? {}) },
        position: d.position,
      }));
      form.lookback_max_months = kpi.lookback_max_months;

      timeEnabled.value = (kpi.time_field_explicit ?? true) && !!kpi.time_field;

      Object.entries(kpi.name ?? {}).forEach(([l, v]) => { nameByLang[l] = v; });
      Object.entries(kpi.description ?? {}).forEach(([l, v]) => { descByLang[l] = v; });

      (form.filters ?? []).forEach((f, i) => {
        if (f.value !== undefined) {
          filterValueProxy[i] = Array.isArray(f.value) ? f.value.join(',') : String(f.value);
        }
      });
    }

    // ----- Step actions -----
    function isSourceSelected(card: any): boolean {
      if (card.schemaName === '__custom_aggregator__') {
        return form.source_type === 'custom' && customDataSources.value.some((s) => s.schemaName === form.source_schema_name);
      }
      return form.source_type === card.type && form.source_schema_name === card.schemaName;
    }

    function selectSource(card: any) {
      if (isEdit.value) return;
      if (card.schemaName === '__custom_aggregator__') {
        // No-op — l'utilisateur doit cliquer sur une sous-carte
        return;
      }
      form.source_type = card.type;
      form.source_schema_name = card.schemaName;
      form.relations_chain = [];
      form.measures = [makeDefaultMeasure()];
      form.dimensions = [];
      form.time_field = '';
      form.time_field_explicit = true;
      form.filters = [];
      timeEnabled.value = false;
      previewResult.value = null;
      loadFields();
    }

    function isRelationSelected(name: string): boolean {
      return (form.relations_chain ?? []).includes(name);
    }

    function toggleRelation(name: string) {
      if (isEdit.value) return;
      const chain = form.relations_chain ?? [];
      if (chain.includes(name)) {
        form.relations_chain = chain.filter((r) => r !== name);
      } else {
        form.relations_chain = [...chain, name];
      }
      loadFields();
    }

    function addMeasure() {
      if (form.measures.length >= MAX_MEASURES_PER_KPI) return;
      form.measures.push(makeDefaultMeasure());
    }

    function removeMeasure(idx: number) {
      if (form.measures.length <= 1) return; // toujours au moins 1
      form.measures.splice(idx, 1);
    }

    function setMeasureType(idx: number, value: AggregationType) {
      const m = form.measures[idx];
      if (!m) return;
      m.type = value;
      if (value === 'count') m.field = undefined;
      else {
        // Si le field actuel n'est plus compatible, on le remet à zéro
        const f = availableFields.value.find((x) => x.path === m.field);
        const compat = f ? AGGREGATIONS_BY_FIELD_TYPE[f.type] ?? [] : [];
        if (m.field && !compat.includes(value)) m.field = undefined;
      }
    }

    function setMeasureField(idx: number, path: string | undefined) {
      const m = form.measures[idx];
      if (!m) return;
      m.field = path;
    }

    function setMeasureLabel(idx: number, lang: string, value: string) {
      const m = form.measures[idx];
      if (!m) return;
      if (!m.label) m.label = {};
      m.label[lang] = value;
    }

    function setMeasureLabelObject(idx: number, value: Record<string, string> | null | undefined) {
      const m = form.measures[idx];
      if (!m) return;
      m.label = { ...(value ?? {}) };
    }

    function setMeasureFormat(
      idx: number,
      value: 'number' | 'currency' | 'percent' | 'integer',
    ) {
      const m = form.measures[idx];
      if (!m) return;
      m.format = value;
    }

    function setMeasureSuffix(idx: number, value: string) {
      const m = form.measures[idx];
      if (!m) return;
      const trimmed = (value ?? '').slice(0, 8).trim();
      m.suffix = trimmed.length > 0 ? trimmed : null;
    }

    const measureFormatOptions = computed(() => [
      {
        value: 'number',
        label: t('customStats.editor.step.measures.formatOptions.number'),
      },
      {
        value: 'currency',
        label: t('customStats.editor.step.measures.formatOptions.currency'),
      },
      {
        value: 'percent',
        label: t('customStats.editor.step.measures.formatOptions.percent'),
      },
      {
        value: 'integer',
        label: t('customStats.editor.step.measures.formatOptions.integer'),
      },
    ]);

    // ---- Dimensions handlers ----
    function addDimension() {
      if (!form.dimensions) form.dimensions = [];
      if (form.dimensions.length >= MAX_DIMENSIONS_PER_KPI) return;
      form.dimensions.push(makeDefaultDimension());
    }

    function removeDimension(idx: number) {
      form.dimensions?.splice(idx, 1);
    }

    function setDimensionField(idx: number, value: string) {
      const d = form.dimensions?.[idx];
      if (!d) return;
      d.field = value;
    }

    function setDimensionLabel(idx: number, lang: string, value: string) {
      const d = form.dimensions?.[idx];
      if (!d) return;
      if (!d.label) d.label = {};
      d.label[lang] = value;
    }

    function setDimensionLabelObject(idx: number, value: Record<string, string> | null | undefined) {
      const d = form.dimensions?.[idx];
      if (!d) return;
      d.label = { ...(value ?? {}) };
    }

    function setTime(enabled: boolean) {
      if (isEdit.value) return;
      timeEnabled.value = enabled;
      form.time_field_explicit = enabled;
      if (!enabled) {
        // Fallback automatique : 1er time field disponible.
        const fallback = timeFieldOptions.value[0]?.path ?? '';
        form.time_field = fallback;
      }
    }

    function addFilter() {
      if ((form.filters?.length ?? 0) >= MAX_FILTERS) return;
      if (!form.filters) form.filters = [];
      form.filters.push({ field: '', operator: 'eq', value: '' });
    }

    function removeFilter(idx: number) {
      form.filters?.splice(idx, 1);
      delete filterValueProxy[idx];
    }

    function filterNeedsValue(op: string): boolean {
      return op !== 'is_null' && op !== 'is_not_null';
    }

    function onFilterValueChange(idx: number, val: string) {
      filterValueProxy[idx] = val;
      if (!form.filters || !form.filters[idx]) return;
      const op = form.filters[idx].operator;
      if (op === 'in' || op === 'not_in') {
        form.filters[idx].value = val.split(',').map((v) => v.trim()).filter(Boolean);
      } else {
        form.filters[idx].value = val;
      }
    }

    function goNextStep() {
      if (currentStepIndex.value < visibleSteps.value.length - 1) {
        currentStepIndex.value += 1;
      }
    }

    function goBackStep() {
      if (currentStepIndex.value > 0) {
        currentStepIndex.value -= 1;
      }
    }

    function buildPayload(): CreateCustomKpiPayload {
      const name: Record<string, string> = {};
      Object.entries(nameByLang).forEach(([l, v]) => { if (v?.trim()) name[l] = v.trim(); });
      const description: Record<string, string> = {};
      Object.entries(descByLang).forEach(([l, v]) => { if (v?.trim()) description[l] = v.trim(); });

      const measures: KpiMeasure[] = form.measures.map((m, i) => {
        const cleanLabel: Record<string, string> = {};
        Object.entries(m.label ?? {}).forEach(([l, v]) => {
          if (v?.trim()) cleanLabel[l] = v.trim();
        });
        return {
          ...(m.id_measure ? { id_measure: m.id_measure } : {}),
          type: m.type,
          field: m.field || undefined,
          format: m.format ?? 'number',
          suffix: m.suffix && m.suffix.length > 0 ? m.suffix : undefined,
          label: cleanLabel,
          position: m.position ?? i,
        };
      });

      const dimensions: KpiDimension[] = (form.dimensions ?? []).map((d, i) => {
        const cleanLabel: Record<string, string> = {};
        Object.entries(d.label ?? {}).forEach(([l, v]) => {
          if (v?.trim()) cleanLabel[l] = v.trim();
        });
        return {
          ...(d.id_dimension ? { id_dimension: d.id_dimension } : {}),
          field: d.field,
          label: cleanLabel,
          position: d.position ?? i,
        };
      });

      return {
        ...form,
        name,
        description: Object.keys(description).length ? description : undefined,
        measures,
        dimensions,
        time_field: form.time_field,
        time_field_explicit: form.time_field_explicit ?? true,
      };
    }

    async function onPreview() {
      isPreviewLoading.value = true;
      previewResult.value = null;
      try {
        const payload = { ...buildPayload(), preview_days: 7 };
        previewResult.value = await previewKpi(payload);
      } catch (err: any) {
        toast.add({ severity: 'error', summary: t('customStats.editor.toast.previewFailed'), detail: err?.message ?? 'Unknown', life: 8000 });
      } finally {
        isPreviewLoading.value = false;
      }
    }

    async function onSave() {
      isSaving.value = true;
      try {
        if (isEdit.value && editId.value) {
          const built = buildPayload();
          const updatePayload = {
            name: built.name,
            description: built.description,
            measures: built.measures,
            dimensions: built.dimensions,
            filters: form.filters,
          };
          await updateKpi(editId.value, updatePayload);
          toast.add({ severity: 'success', summary: 'OK', detail: t('customStats.editor.toast.updated'), life: 3000 });
        } else {
          await createKpi(buildPayload());
          toast.add({ severity: 'success', summary: 'OK', detail: t('customStats.editor.toast.created'), life: 3000 });
        }
        router.push({ name: 'custom-kpis.list' });
      } catch (err: any) {
        toast.add({ severity: 'error', summary: t('customStats.editor.toast.saveFailed'), detail: err?.message ?? 'Unknown', life: 8000 });
      } finally {
        isSaving.value = false;
      }
    }

    function goBack() {
      router.push({ name: 'custom-kpis.list' });
    }

    // ===== Templates de KPIs prêts à cloner =====
    const templatesModalOpen = ref(false);

    const templateGroups = computed(() => {
      const buckets: Record<string, CustomKpiTemplate[]> = {};
      for (const tpl of CUSTOM_KPI_TEMPLATES) {
        if (!buckets[tpl.group]) buckets[tpl.group] = [];
        buckets[tpl.group].push(tpl);
      }
      return CUSTOM_KPI_TEMPLATE_GROUP_ORDER
        .filter((key) => buckets[key]?.length)
        .map((key) => ({
          key,
          label: t(`customStats.templates.groups.${key}`),
          templates: buckets[key],
        }));
    });

    function openTemplatesModal() {
      templatesModalOpen.value = true;
    }

    async function applyTemplate(tpl: CustomKpiTemplate) {
      // On replace le form en partant du template (le name reste à la
      // discrétion du marchand). Toutes les listes sont clonées pour
      // éviter qu'éditer le form ne mute le template d'origine.
      const p = tpl.payload;
      form.source_type = p.source_type;
      form.source_schema_name = p.source_schema_name;
      form.relations_chain = [...(p.relations_chain ?? [])];
      form.measures = (p.measures ?? []).map((m) => ({
        ...m,
        label: { ...m.label },
      }));
      form.dimensions = (p.dimensions ?? []).map((d) => ({
        ...d,
        label: { ...d.label },
      }));
      form.time_field = p.time_field;
      form.time_field_explicit = p.time_field_explicit ?? true;
      form.filters = (p.filters ?? []).map((f) => ({ ...f }));
      timeEnabled.value = !!form.time_field;
      previewResult.value = null;
      templatesModalOpen.value = false;
      // Charge les fields de la nouvelle source pour que le wizard puisse
      // afficher / valider les paths référencés par le template.
      await loadFields();
    }

    return {
      t,
      MAX_FILTERS,
      MAX_LOOKBACK_MONTHS,
      MAX_MEASURES_PER_KPI,
      MAX_DIMENSIONS_PER_KPI,
      shopLangs,
      // form & state
      form,
      nameByLang,
      descByLang,
      filterValueProxy,
      timeEnabled,
      availableFields,
      isEdit,
      isSaving,
      // step
      currentStep,
      currentStepNumber,
      visibleSteps,
      isLastStep,
      canGoBack,
      canGoNext,
      progressPercent,
      canSave,
      canRunPreview,
      // titles
      modalTitle,
      modalSubtitle,
      // cards
      sourceCards,
      sourceGroups,
      hasOneToManyRelationSelected,
      // templates
      templatesModalOpen,
      templateGroups,
      openTemplatesModal,
      applyTemplate,
      // tooltips
      fieldHint,
      // preview cost
      formatNumber,
      estimatedYearlyRows,
      customDataSources,
      availableRelations,
      aggregationCardsForMeasure,
      aggregationFieldOptionsForMeasure,
      dimensionFieldOptions,
      timeFieldOptions,
      filterOperatorOptions,
      // helpers
      typeLabel,
      displayLabel,
      aggregationLabel,
      isSourceSelected,
      isRelationSelected,
      filterNeedsValue,
      // summary
      summarySource,
      summaryRelations,
      summaryMeasures,
      summaryDimensions,
      summaryTime,
      summaryFilters,
      // preview
      isPreviewLoading,
      previewResult,
      onPreview,
      // step actions
      selectSource,
      toggleRelation,
      addMeasure,
      removeMeasure,
      setMeasureType,
      setMeasureField,
      setMeasureLabel,
      setMeasureLabelObject,
      setMeasureFormat,
      setMeasureSuffix,
      measureFormatOptions,
      addDimension,
      removeDimension,
      setDimensionField,
      setDimensionLabel,
      setDimensionLabelObject,
      setTime,
      addFilter,
      removeFilter,
      onFilterValueChange,
      goNextStep,
      goBackStep,
      onSave,
      goBack,
    };
  },
});
